import React from 'react';
import MarkdownMessage from '@/components/common/MarkdownMessage';
import { detectMessageType } from '@/utils/copilot/messageTypeDetector';
import { FormMessage } from './FormMessage';
import { StructuredFormMessage } from './StructuredFormMessage';
import { StructuredFormMessageTest } from './StructuredFormMessageTest';
import { ActionResultMessage } from './ActionResultMessage';
import { ListResultsMessage } from './ListResultsMessage';
import { ClarificationMessage } from './ClarificationMessage';
import { AnalyticsRankingMessage } from './AnalyticsRankingMessage';
import { ReasoningPipelineMessage } from './ReasoningPipelineMessage';
import { ActionCard } from '@/components/copilot/chat/cards/ActionCard';
import { ActionCardData } from '@/types/action-card';
import { MessageMetadataBadge } from './MessageMetadataBadge';
import { ToolResultCard } from '../tool-results/ToolResultCard';
import { ExecutionDetails } from './ExecutionDetails';
import { PlanStepper } from './PlanStepper';
import type { ExecutionDetail, PlanStep } from '@/components/copilot/mcp/chatTypes';

// Error boundary component for form rendering
class FormErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('❌ [FormErrorBoundary] Caught error in form rendering:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      console.error('❌ [FormErrorBoundary] Rendering fallback due to error:', this.state.error);
      return this.props.fallback;
    }

    return this.props.children;
  }
}

interface MessageMetadata {
  confidence?: number;
  safety_active?: boolean;
  response_time?: number;
  tool_used?: string;
}

interface MessageRendererProps {
  content: string;
  role: 'user' | 'assistant';
  structured_data?: any;
  action_card_data?: ActionCardData;
  execution_details?: ExecutionDetail[];
  plan_steps?: PlanStep[];
  metadata?: MessageMetadata;
  onLinkClick?: (href: string) => void;
  onFormSubmit?: (formData: Record<string, string>) => void;
  onActionClick?: (action: string, itemId: string, itemData?: any) => void;
  onSuggestionClick?: (suggestion: string) => void;
}

/**
 * Smart message renderer that chooses appropriate component based on content
 */
export const MessageRenderer: React.FC<MessageRendererProps> = ({
  content,
  role,
  structured_data,
  action_card_data,
  execution_details,
  plan_steps,
  metadata,
  onLinkClick,
  onFormSubmit,
  onActionClick,
  onSuggestionClick
}) => {
  // If action_card_data is provided, render action card
  if (action_card_data) {
    return (
      <div className="space-y-2">
        {content && (
          <MarkdownMessage className="text-gray-600 mb-2" onLinkClick={onLinkClick}>{content}</MarkdownMessage>
        )}
        <ActionCard data={action_card_data} />
      </div>
    );
  }

  // For user messages, always use simple rendering
  if (role === 'user') {
    return <div className="whitespace-pre-wrap">{content}</div>;
  }

  // Debug what MessageRenderer actually receives
  console.log('🔧 [MessageRenderer] RECEIVED message:', {
    content: content.substring(0, 100) + (content.length > 100 ? '...' : ''),
    role,
    has_structured_data: !!structured_data,
    structured_data_preview: structured_data ? JSON.stringify(structured_data, null, 2).substring(0, 500) : null
  });
  
  // For assistant messages, detect type and render accordingly
  const messageInfo = detectMessageType(content, structured_data);
  
  console.log('🔧 [MessageRenderer] Message type detection result:', {
    detected_type: messageInfo.type,
    response_type: structured_data?.response_type,
    has_data: !!structured_data?.data,
    structured_data_keys: structured_data ? Object.keys(structured_data) : [],
    will_render_form: messageInfo.type === 'form_request' && (structured_data?.data || structured_data?.response_type),
    messageInfo_structured_data: messageInfo.structured_data,
    original_structured_data: structured_data
  });

  switch (messageInfo.type) {
    case 'form_request':
      console.log('🔧 [MessageRenderer] Form request case reached');
      console.log('🔧 [MessageRenderer] Structured data check:', {
        has_structured_data: !!structured_data,
        has_data: !!structured_data?.data,
        has_response_type: !!structured_data?.response_type,
        response_type: structured_data?.response_type,
        structured_data_keys: structured_data ? Object.keys(structured_data) : [],
        structured_data: structured_data
      });
      
      // Safety check: only render form if we have actual structured data
      if (!structured_data?.data && !structured_data?.response_type) {
        console.log('🔧 [MessageRenderer] No structured form data, falling back to text message');
        return (
          <MarkdownMessage onLinkClick={onLinkClick}>{content}</MarkdownMessage>
        );
      }
      
      console.log('🔧 [MessageRenderer] Using incremental StructuredFormMessage');
      return (
        <FormErrorBoundary
          fallback={
            <MarkdownMessage onLinkClick={onLinkClick}>{content}</MarkdownMessage>
          }
        >
          <StructuredFormMessage 
            content={content}
            structured_data={structured_data}
            onSubmit={onFormSubmit}
          />
        </FormErrorBoundary>
      );

    case 'list_results':
      return (
        <ListResultsMessage 
          content={content}
          structured_data={messageInfo.structured_data}
          onActionClick={onActionClick}
        />
      );

    case 'empty_state':
      return (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
          <div className="text-4xl mb-2">
            {structured_data?.data?.icon || '📭'}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {structured_data?.data?.title || 'No Results'}
          </h3>
          <p className="text-gray-600 mb-4">
            {structured_data?.data?.message || 'No items found.'}
          </p>
          {structured_data?.data?.suggestions && structured_data.data.suggestions.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Suggestions:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                {structured_data.data.suggestions.map((suggestion: string, index: number) => (
                  <li key={index}>• {suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );

    case 'create_success':
    case 'update_success':
    case 'delete_success':
      return (
        <ActionResultMessage 
          content={content}
          status={messageInfo.metadata?.resultStatus || 'success'}
          hasLinks={messageInfo.metadata?.hasLinks}
          colorHexes={messageInfo.metadata?.colorHexes}
          structured_data={structured_data || messageInfo.structured_data}
          onLinkClick={onLinkClick}
        />
      );

    case 'error':
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2 text-red-800">
            <div className="text-red-500">❌</div>
            <MarkdownMessage onLinkClick={onLinkClick}>{content}</MarkdownMessage>
          </div>
        </div>
      );

    case 'clarification_request':
      return (
        <ClarificationMessage
          content={content}
          structured_data={messageInfo.structured_data}
          onSuggestionClick={onSuggestionClick}
          onLinkClick={onLinkClick}
        />
      );

    case 'analytics_ranking':
      return (
        <AnalyticsRankingMessage
          content={content}
          structured_data={structured_data || messageInfo.structured_data}
        />
      );

    case 'reasoning_pipeline':
      return (
        <ReasoningPipelineMessage
          content={content}
          metadata={messageInfo.metadata}
        />
      );

    case 'greeting_response':
      return (
        <div className="space-y-4">
          <MarkdownMessage onLinkClick={onLinkClick}>{content}</MarkdownMessage>

          {/* Render suggestion buttons if available */}
          {structured_data?.suggestions && Array.isArray(structured_data.suggestions) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {structured_data.suggestions.map((suggestion: string, index: number) => (
                <button
                  key={index}
                  onClick={() => onSuggestionClick?.(suggestion)}
                  className="px-3 py-2 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border border-blue-200 transition-colors duration-200"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>
      );

    case 'tool_result':
      // Legacy path: if formatted_response is present without execution_details,
      // still render the old ToolResultCard for backwards compatibility with
      // persisted/historical messages.
      if (structured_data?.formatted_response && !execution_details?.length) {
        return (
          <ToolResultCard
            tool_name={structured_data.tool_name}
            action_name={structured_data.action_name}
            formatted_response={structured_data.formatted_response}
            onLinkClick={onLinkClick}
          />
        );
      }
      // Fall through so a tool reply with execution details keeps its text.

    case 'text_message':
    case 'general':
    default:
      return (
        <div className="space-y-2">
          {plan_steps && plan_steps.length > 0 && (
            <PlanStepper steps={plan_steps} />
          )}
          {content && (
            <MarkdownMessage onLinkClick={onLinkClick}>{content}</MarkdownMessage>
          )}
          {execution_details && execution_details.length > 0 && (
            <ExecutionDetails details={execution_details} />
          )}
          {metadata && <MessageMetadataBadge metadata={metadata} />}
        </div>
      );
  }
};
